/**
 * Performance Tracking Helpers
 *
 * Safe utility functions for tracking performance metrics.
 * All functions follow the pattern: check enabled → try-catch → silent failure
 */

import { isTroubleshootingEnabled } from './storageUtils';
import { getTroubleshootingTracker } from './TroubleshootingTracker';
import { TrackingEventType, TrackingFeature } from './types';

/**
 * Track form API completion (GET or DA)
 * Only tracks completion events (success/error) to avoid orphan start events
 *
 * @param type - 'GET' for form/get calls, 'DA' for form/da calls
 * @param formId - The form ID or slug
 * @param status - 'success' or 'error'
 * @param duration - Duration in milliseconds
 * @param error - Error message (optional, for error status)
 */
export function trackFormApiCompletion(
  type: 'GET' | 'DA',
  formId: string,
  status: 'success' | 'error',
  duration: number,
  error?: string
): void {
  // Guard 1: Early exit if troubleshooting is disabled
  if (!isTroubleshootingEnabled()) {
    return;
  }

  // Guard 2: Try-catch wrapper for safety
  try {
    const tracker = getTroubleshootingTracker();

    // Guard 3: Check tracker is enabled
    if (!tracker.isEnabled()) {
      return;
    }

    // Map type and status to event type
    let eventType: TrackingEventType;

    if (type === 'GET') {
      eventType =
        status === 'success'
          ? TrackingEventType.FORM_API_GET_SUCCESS
          : TrackingEventType.FORM_API_GET_ERROR;
    } else {
      eventType =
        status === 'success'
          ? TrackingEventType.FORM_API_DA_SUCCESS
          : TrackingEventType.FORM_API_DA_ERROR;
    }

    tracker.track(
      TrackingFeature.FORM_API,
      eventType,
      {
        type,
        formId,
        duration,
        status,
        ...(error && { error }),
        timestamp: Date.now(),
      },
      duration
    );
  } catch {
    // Guard 4: Silent failure - tracking should never break the app
  }
}

/**
 * Track form render completion
 *
 * @param fieldCount - Number of fields rendered
 * @param duration - Duration in milliseconds
 * @param urlSlug - Optional form URL slug
 */
export function trackFormRenderCompletion(
  fieldCount: number,
  duration: number,
  urlSlug?: string
): void {
  // Guard 1: Early exit if troubleshooting is disabled
  if (!isTroubleshootingEnabled()) {
    return;
  }

  // Guard 2: Try-catch wrapper for safety
  try {
    const tracker = getTroubleshootingTracker();

    // Guard 3: Check tracker is enabled
    if (!tracker.isEnabled()) {
      return;
    }

    tracker.track(
      TrackingFeature.FORM_RENDER,
      TrackingEventType.FORM_RENDER_END,
      {
        fieldCount,
        duration,
        ...(urlSlug && { urlSlug }),
        timestamp: Date.now(),
      },
      duration
    );
  } catch {
    // Guard 4: Silent failure - tracking should never break the app
  }
}

/**
 * Get MFE performance metrics from browser Performance API
 * Returns timing data for bootstrap, mount, and total load time
 */
export function getMfePerformanceMetrics(): {
  bootstrapTime?: number;
  mountTime?: number;
  totalTime?: number;
} {
  try {
    // Check if Performance API is available
    if (typeof performance === 'undefined') {
      return {};
    }

    const bootstrapMeasure = performance.getEntriesByName(
      'ff-mfe-bootstrap',
      'measure'
    )[0] as PerformanceMeasure | undefined;

    const mountMeasure = performance.getEntriesByName(
      'ff-mfe-mount',
      'measure'
    )[0] as PerformanceMeasure | undefined;

    const totalMeasure = performance.getEntriesByName(
      'ff-mfe-total',
      'measure'
    )[0] as PerformanceMeasure | undefined;

    return {
      bootstrapTime: bootstrapMeasure?.duration,
      mountTime: mountMeasure?.duration,
      totalTime: totalMeasure?.duration,
    };
  } catch {
    // Performance API not available or error occurred
    return {};
  }
}
